import React from "react";
import { useTranslation } from "react-i18next";
import { BookingLanguage } from "@/types/booking";
import { useMembership } from "@/contexts/MembershipContext";
import { languageMap } from "@/i18n";

interface MemberHeaderBadgeProps {
  language: BookingLanguage;
}

/**
 * Small pill shown under the header logo row on every step, signalling that
 * the booking flow recognised an active membership (via ?membershipToken=).
 * Shows the member's name and email when the membership data includes them.
 */
const MemberHeaderBadge = ({ language }: MemberHeaderBadgeProps) => {
  const { t } = useTranslation(undefined, { lng: languageMap[language] });
  const { isMember, membership } = useMembership();

  if (!isMember) return null;

  const fullName = [membership?.first_name, membership?.last_name]
    .filter(Boolean)
    .join(" ");
  // Show the name when available; fall back to the email so the pill stays short
  const identity = fullName || membership?.email || "";

  return (
    <div className="container mx-auto px-4 pb-1">
      <div
        data-testid="member-header-badge"
        className="w-fit mx-auto flex flex-wrap items-center justify-center gap-2 bg-deep-green-100 rounded-full px-3 py-1.5"
      >
        <svg
          className="shrink-0"
          width="14"
          height="14"
          viewBox="0 0 20 20"
          fill="none"
          xmlns="http://www.w3.org/2000/svg"
        >
          <path
            d="M16.667 5L7.5 14.167 3.333 10"
            stroke="#26542B"
            strokeWidth="2.5"
            strokeLinecap="round"
            strokeLinejoin="round"
          />
        </svg>
        <p className="text-deep-green-700 text-sm">
          <span className="font-medium">{t("header.memberBadge")}</span>
          {identity && (
            <span className="font-normal break-all"> · {identity}</span>
          )}
        </p>
      </div>
    </div>
  );
};

export default MemberHeaderBadge;
